import { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { PanierContext } from "../Store/Panier_context";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ValiderSuppression from "./ValiderSuppression";
import { AuthContext } from "./AuthContext";
import { getProductImage } from "../Utils/Cloudinary";
import CheckoutStepper from "./CheckoutStepper";
import CartWeightAdjuster from "./CartWeightAdjuster";
import { isWeightSaleProduct } from "../Utils/weightSale";
import {
  IconTrash,
  IconMinus,
  IconPlus,
  IconShoppingCartOff,
  IconTicket,
  IconLock,
} from "@tabler/icons-react";
import "../Styles/Cart.css";
import {
  faTruckFast,
  faShieldHalved,
  faHeadset,
  faRotateLeft,
} from "@fortawesome/free-solid-svg-icons";

const CHECKOUT_STEPS = ["Panier", "Adresse", "Paiement"];

const Cart = () => {
  const { products, updateProductQuantity, updateProductWeight } = useContext(PanierContext);
  const [showPopUp, setShowPopUp] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { isLoggedIn } = useContext(AuthContext);

  const openPopUp = () => setShowPopUp(true);
  const closePopUp = () => setShowPopUp(false);

  const formatPrice = (price) => {
    const numericValue = typeof price === "string" ? parseFloat(price) : price;
    return new Intl.NumberFormat("fr-FR", {
      style: "decimal",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numericValue);
  };

  const totalPrice = products.reduce(
    (acc, product) => acc + product.price * product.quantity,
    0,
  );

  const handleResponse = () => {
    toast.error("Votre panier est vide !");
  };

  const handleNavigate = () => {
    setLoading(true);
    navigate("/paiement");
    setLoading(false);
  };

  const handleRedirect = () => {
    navigate("/login");
  };

  const handleContinueShopping = () => {
    navigate("/all_products");
  };

  const TRUST_ITEMS = [
    {
      icon: faTruckFast,
      title: "Livraison rapide",
      text: "Partout à Cotonou et environs",
    },
    {
      icon: faShieldHalved,
      title: "Paiement sécurisé",
      text: "Mobile Money, carte bancaire",
    },
    {
      icon: faHeadset,
      title: "Support 7j/7",
      text: "Une question ? On répond vite",
    },
    {
      icon: faRotateLeft,
      title: "Retours faciles",
      text: "Sous 48h après réception",
    },
  ];

  return (
    <div className="cart-page">
      <div className="container-fluid">
        <CheckoutStepper steps={CHECKOUT_STEPS} currentStep={1} />

        <div className="row">
          {/* ================= LISTE DES PRODUITS ================= */}
          <div className="col-lg-8 col-12 mb-4" data-tour="cart">
            <div className="cart-card">
              <div className="cart-card-header">
                <h2 className="cart-card-title">
                  Mon panier
                  {products.length > 0 && (
                    <span className="cart-item-count">
                      {products.length} article{products.length > 1 ? "s" : ""}
                    </span>
                  )}
                </h2>
                {products.length > 0 && (
                  <button className="cart-clear-link" onClick={openPopUp} type="button">
                    <IconTrash size={15} />
                    Vider le panier
                  </button>
                )}
              </div>

              <hr />

              {products.length === 0 ? (
                <div className="cart-empty">
                  <IconShoppingCartOff size={56} />
                  <p className="cart-empty-title">Votre panier est vide</p>
                  <p className="cart-empty-text">
                    Parcourez nos rayons et ajoutez des produits pour commencer vos achats.
                  </p>
                  <button className="cart-empty-btn" onClick={handleContinueShopping}>
                    Continuer mes achats
                  </button>
                </div>
              ) : (
                <div className="cart-products">
                  {products.map((product) => {
                    const weightSale = isWeightSaleProduct(product);

                    return (
                      <div className="cart-product-row" key={product.id}>
                        <div className="cart-cell-img">
                          <img alt={product.name} src={getProductImage(product.image)} />
                        </div>

                        <div className="cart-cell-info">
                          <div className="cart-product-brand">{product.category}</div>
                          <div className="cart-product-name">{product.name}</div>
                          <div className="cart-product-meta">
                            {product.weightLabel && (
                              <span className="cart-weight-badge">{product.weightLabel}</span>
                            )}
                            {product.type && <span>{product.type}</span>}
                            {product.disponibilité && (
                              <span>Disponibilité : {product.disponibilité}</span>
                            )}
                          </div>
                        </div>

                        <div className="cart-cell-price">
                          <span className="cart-cell-label">
                            {weightSale ? "Prix au kg" : "Prix unitaire"}
                          </span>
                          {weightSale
                            ? `${formatPrice(product.pricePerKg)} FCFA`
                            : `${formatPrice(product.price)} FCFA`}
                        </div>

                        <div className="cart-cell-qty">
                          <span className="cart-cell-label">
                            {weightSale ? "Ajuster" : "Quantité"}
                          </span>
                          {weightSale ? (
                            <CartWeightAdjuster
                              pricePerKg={product.pricePerKg}
                              currentWeightLabel={product.weightLabel}
                              onChange={({ price, weightLabel }) =>
                                updateProductWeight(product.id, price, weightLabel)
                              }
                            />
                          ) : (
                            <div className="cart-qty-stepper">
                              <button
                                onClick={() => updateProductQuantity(product.id, -1)}
                                aria-label="Diminuer la quantité"
                              >
                                <IconMinus size={14} />
                              </button>
                              <span className="cart-qty-value">{product.quantity}</span>
                              <button
                                onClick={() => updateProductQuantity(product.id, 1)}
                                disabled={product.quantity >= product.stock}
                                aria-label="Augmenter la quantité"
                              >
                                <IconPlus size={14} />
                              </button>
                            </div>
                          )}
                        </div>

                        <div className="cart-cell-total">
                          <span className="cart-cell-label">Total</span>
                          <strong>{formatPrice(product.price * product.quantity)} FCFA</strong>
                        </div>

                        <button
                          className="cart-cell-del"
                          onClick={() => updateProductQuantity(product.id, -product.quantity)}
                          title="Supprimer ce produit"
                          aria-label="Supprimer ce produit"
                        >
                          <IconTrash size={17} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* ================= RÉCAPITULATIF ================= */}
          <div className="col-lg-4 col-12 mb-4">
            <div className="cart-summary-card">
              <div className="cart-coupon">
                <label className="cart-coupon-label" htmlFor="coupon">
                  <IconTicket size={16} />
                  Code de points bonus
                </label>
                <div className="cart-coupon-row">
                  <input id="coupon" type="text" placeholder="XXX-XXX" />
                  <button type="button">Appliquer</button>
                </div>
              </div>

              <div className="cart-totals">
                <div className="cart-totals-row">
                  <span>Sous-total</span>
                  <span>{formatPrice(totalPrice)} FCFA</span>
                </div>
                <div className="cart-totals-row">
                  <span>Livraison</span>
                  <span>Calculée à l'étape suivante</span>
                </div>
                <div className="cart-totals-row cart-total-final">
                  <span>Total</span>
                  <span className="cart-total-amount">{formatPrice(totalPrice)} FCFA</span>
                </div>
              </div>

              {totalPrice < 1 ? (
                <button className="cart-checkout-btn" onClick={handleResponse}>
                  <IconLock size={17} />
                  Passer la commande
                </button>
              ) : isLoggedIn ? (
                <button className="cart-checkout-btn" onClick={handleNavigate} disabled={loading}>
                  {loading ? (
                    <Spinner size="sm" animation="border" />
                  ) : (
                    <>
                      <IconLock size={17} />
                      Passer la commande
                    </>
                  )}
                </button>
              ) : (
                <>
                  <button className="cart-checkout-btn" onClick={handleRedirect}>
                    Se connecter pour commander
                  </button>
                  <p className="cart-login-hint">
                    Vous devez être connecté pour finaliser votre achat.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>


      </div>
      <div className="nm-footer">
        <div className="nm-footer-trust">
          <div className="container-fluid">
            <div className="nm-trust-grid">
              {TRUST_ITEMS.map((item, i) => (
                <div className="nm-trust-item" key={i}>
                  <FontAwesomeIcon icon={item.icon} className="nm-trust-icon" />
                  <div>
                    <p className="nm-trust-title">{item.title}</p>
                    <p className="nm-trust-text">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {showPopUp && <ValiderSuppression closePopUp={closePopUp} />}
    </div>
  );
};

export default Cart;
